import moment from 'moment-timezone'
import { FILTER_TYPES, getFilterByKey } from './filterDefinitions.js'

const DATE_FMT = 'MM/DD/YYYY'

/**
 * Filter Transformers
 *
 * Transform filter values to API query parameters.
 */

/**
 * Transform a filter object to query parameters
 *
 * @param {{ key: string, value: * }} filter
 * @param {{ selectedMarkets?: string[], availableMarkets?: Array }} [marketContext]
 *   Selected market uids and the markets list; the date filter resolves its
 *   day boundaries in the selected markets' timezones
 */
export const transformFilterToQueryProps = (filter, marketContext = {}) => {
  const { key, value } = filter

  switch (key) {
    case FILTER_TYPES.DATE:
      return transformDateFilter(value, marketContext)

    case FILTER_TYPES.IMC:
      return transformImcFilter(value)

    case FILTER_TYPES.CARRIER:
      return transformCarrierFilter(value)

    case FILTER_TYPES.DRIVER:
      return transformDriverFilter(value)

    case FILTER_TYPES.EDI:
      return transformEdiFilter(value)

    case FILTER_TYPES.STREET_TURNS:
      return transformStreetTurnsFilter(value)

    case FILTER_TYPES.CREATED_BY:
      return transformCreatedByFilter(value)

    case FILTER_TYPES.LANE_LABEL:
      return transformLaneLabelFilter(value)

    case FILTER_TYPES.FREIGHT_TYPE:
      return transformFreightTypeFilter(value)

    case FILTER_TYPES.PENDING_DRIVER_ACCEPTANCE:
      return transformPendingDriverAcceptanceFilter(value)

    default:
      return {}
  }
}

export const getTimezonesFromMarkets = (
  selectedMarkets,
  availableMarkets,
  dateStr,
) => {
  const selected =
    selectedMarkets.length > 0
      ? availableMarkets.filter((m) => selectedMarkets.includes(m.uid))
      : availableMarkets

  // markets.list hides inactive, hidden and cross-domain markets, but a user
  // can still be joined to one and carry its uid in the URL. Resolving to
  // nothing must degrade to the same every-market span as selecting nothing —
  // the alternative is no timezone at all, which silently computes the day in
  // the browser's zone instead of a market's.
  const relevantMarkets = selected.length > 0 ? selected : availableMarkets

  const timezones = relevantMarkets.map((m) => m.timezone).filter(Boolean)

  if (timezones.length === 0) return { startTz: null, endTz: null }
  if (timezones.length === 1)
    return { startTz: timezones[0], endTz: timezones[0] }

  let startTz = timezones[0]
  let endTz = timezones[0]
  timezones.forEach((tz) => {
    const offset = moment.tz(dateStr, DATE_FMT, tz).utcOffset()
    if (offset > moment.tz(dateStr, DATE_FMT, startTz).utcOffset()) startTz = tz
    if (offset < moment.tz(dateStr, DATE_FMT, endTz).utcOffset()) endTz = tz
  })
  return { startTz, endTz }
}

/**
 * Transform date range filter
 * Input: "12/01/2025 - 12/15/2025" or single date (locale 'L' format)
 */
const transformDateFilter = (value, marketContext = {}) => {
  if (!value) return {}

  const { selectedMarkets = [], availableMarkets = [] } = marketContext

  const dateRange = value.split(' - ')

  const tzStart = (dateStr, tz) =>
    tz
      ? moment.tz(dateStr, DATE_FMT, tz).startOf('day').toISOString()
      : moment(dateStr, DATE_FMT).startOf('day').toISOString()
  const tzEnd = (dateStr, tz) =>
    tz
      ? moment.tz(dateStr, DATE_FMT, tz).endOf('day').toISOString()
      : moment(dateStr, DATE_FMT).endOf('day').toISOString()

  if (dateRange.length === 2) {
    const { startTz } = getTimezonesFromMarkets(
      selectedMarkets,
      availableMarkets,
      dateRange[0],
    )
    const { endTz } = getTimezonesFromMarkets(
      selectedMarkets,
      availableMarkets,
      dateRange[1],
    )

    return {
      appointmentEndAfter: tzStart(dateRange[0], startTz),
      appointmentStartBefore: tzEnd(dateRange[1], endTz),
    }
  }

  const { startTz } = getTimezonesFromMarkets(
    selectedMarkets,
    availableMarkets,
    value,
  )
  const { endTz } = getTimezonesFromMarkets(
    selectedMarkets,
    availableMarkets,
    value,
  )

  return {
    appointmentEndAfter: tzStart(value, startTz),
    appointmentStartBefore: tzEnd(value, endTz),
  }
}

/**
 * Transform IMC company filter (multi-select)
 */
const transformImcFilter = (value) => {
  if (!value || (Array.isArray(value) && value.length === 0)) return {}

  const uids = Array.isArray(value)
    ? value.map((v) => v.uid || v)
    : [value.uid || value]

  return {
    imcCompanyUid: uids,
  }
}

/**
 * Transform carrier company filter (single select)
 */
const transformCarrierFilter = (value) => {
  if (!value) return {}

  return {
    carrierCompanyUid: value.uid || value,
  }
}

/**
 * Transform driver filter (single select)
 */
const transformDriverFilter = (value) => {
  if (!value) return {}

  return {
    driverUserUid: value.uid || value,
  }
}

/**
 * Transform EDI filter (boolean)
 */
const transformEdiFilter = (value) => {
  return {
    ediOrders: Boolean(value),
  }
}

/**
 * Transform street turns filter (checkbox group)
 */
const transformStreetTurnsFilter = (value) => {
  if (!value || typeof value !== 'object') return {}

  return {
    bundledStreetTurns: value.bundledStreetTurns ?? true,
    interchanges: value.interchanges ?? true,
    ordersInBundle: value.ordersInBundle ?? true,
  }
}

/**
 * Transform created by filter (multi-select users)
 */
const transformCreatedByFilter = (value) => {
  if (!value || (Array.isArray(value) && value.length === 0)) return {}

  const uids = Array.isArray(value)
    ? value.map((v) => v.uid || v)
    : [value.uid || value]

  return {
    createdBy: uids,
  }
}

/**
 * Transform lane label filter (checkbox group)
 */
const transformLaneLabelFilter = (value) => {
  if (!value || typeof value !== 'object') return {}

  return {
    laneAwarded: value.laneAwarded ?? true,
    laneConsistent: value.laneConsistent ?? true,
    laneNoLabel: value.laneNoLabel ?? true,
  }
}

/**
 * Transform freight type filter (single select)
 */
const transformFreightTypeFilter = (value) => {
  if (!value) return {}

  return {
    freightType: [value.value || value],
  }
}

/**
 * Transform pending driver acceptance filter (boolean)
 */
const transformPendingDriverAcceptanceFilter = (value) => {
  return {
    state: 'pending_driver',
  }
}

/**
 * Merge every filter's query props into a single object
 *
 * @param {Array<{ key: string, value: * }>} filters
 * @param {{ selectedMarkets?: string[], availableMarkets?: Array }} [marketContext]
 *   See transformFilterToQueryProps
 */
export const mergeFilterQueryProps = (filters, marketContext = {}) =>
  filters.reduce(
    (acc, filter) => ({
      ...acc,
      ...transformFilterToQueryProps(filter, marketContext),
    }),
    {},
  )

/**
 * URL param prefix for filter params
 */
const FILTER_PREFIX = 'f_'

/**
 * URL param keys for each filter type
 */
const FILTER_PARAM_KEYS = {
  [FILTER_TYPES.DATE]: `${FILTER_PREFIX}date`,
  [FILTER_TYPES.IMC]: `${FILTER_PREFIX}imc`,
  [FILTER_TYPES.CARRIER]: `${FILTER_PREFIX}carrier`,
  [FILTER_TYPES.DRIVER]: `${FILTER_PREFIX}driver`,
  [FILTER_TYPES.EDI]: `${FILTER_PREFIX}edi`,
  [FILTER_TYPES.STREET_TURNS]: `${FILTER_PREFIX}streetTurns`,
  [FILTER_TYPES.CREATED_BY]: `${FILTER_PREFIX}createdBy`,
  [FILTER_TYPES.LANE_LABEL]: `${FILTER_PREFIX}laneLabel`,
  [FILTER_TYPES.FREIGHT_TYPE]: `${FILTER_PREFIX}freightType`,
  [FILTER_TYPES.PENDING_DRIVER_ACCEPTANCE]: `${FILTER_PREFIX}pendingDriverAcceptance`,
}

/**
 * Reverse lookup: URL param key → filter type key
 */
const PARAM_TO_FILTER_KEY = Object.entries(FILTER_PARAM_KEYS).reduce(
  (acc, [filterKey, paramKey]) => {
    acc[paramKey] = filterKey
    return acc
  },
  {},
)

/**
 * Extract a display name from an entity object safely.
 * Handles user objects (firstName/lastName), companies (name/fullName), and strings.
 */
const getEntityDisplayName = (entity) => {
  if (!entity || typeof entity === 'string') return entity || '-'
  return (
    entity.name ||
    entity.fullName ||
    [entity.firstName, entity.lastName].filter(Boolean).join(' ') ||
    entity.uid ||
    '-'
  )
}

/**
 * Serialize an entity value (object with uid/name) to "uid|name" string
 */
const serializeEntity = (entity) => {
  if (!entity) return ''
  const uid = entity.uid || entity
  const name =
    entity.name ||
    entity.fullName ||
    [entity.firstName, entity.lastName].filter(Boolean).join(' ') ||
    ''
  return name ? `${uid}|${name}` : String(uid)
}

/**
 * Deserialize "uid|name" string to { uid, name } object
 */
const deserializeEntity = (str) => {
  if (!str) return null
  const pipeIndex = str.indexOf('|')
  if (pipeIndex === -1) return { uid: str, name: str }
  return {
    uid: str.substring(0, pipeIndex),
    name: str.substring(pipeIndex + 1),
  }
}

/**
 * Serialize appliedFilters array → plain object of URL param entries
 * Returns: { f_date: '...', f_imc: '...', ... }
 */
export const serializeFiltersToParams = (appliedFilters) => {
  const params = {}

  for (const filter of appliedFilters) {
    const paramKey = FILTER_PARAM_KEYS[filter.key]
    if (!paramKey) continue

    switch (filter.key) {
      case FILTER_TYPES.DATE:
        if (filter.value) params[paramKey] = filter.value
        break

      case FILTER_TYPES.IMC:
      case FILTER_TYPES.CREATED_BY: {
        // Multi-select entity: semicolon-separated uid|name pairs
        const values = Array.isArray(filter.value)
          ? filter.value
          : [filter.value]
        const serialized = values.map(serializeEntity).filter(Boolean).join(';')
        if (serialized) params[paramKey] = serialized
        break
      }

      case FILTER_TYPES.CARRIER:
      case FILTER_TYPES.DRIVER: {
        // Single entity: uid|name
        const serialized = serializeEntity(filter.value)
        if (serialized) params[paramKey] = serialized
        break
      }

      case FILTER_TYPES.EDI:
      case FILTER_TYPES.PENDING_DRIVER_ACCEPTANCE:
        params[paramKey] = String(Boolean(filter.value))
        break

      case FILTER_TYPES.STREET_TURNS:
      case FILTER_TYPES.LANE_LABEL: {
        // Checkbox group: comma-separated keys of truthy values
        if (filter.value && typeof filter.value === 'object') {
          const enabledKeys = Object.entries(filter.value)
            .filter(([, v]) => v)
            .map(([k]) => k)
          if (enabledKeys.length > 0) params[paramKey] = enabledKeys.join(',')
        }
        break
      }

      case FILTER_TYPES.FREIGHT_TYPE: {
        // Single entity-like: value|name
        if (filter.value) {
          const val = filter.value.value || filter.value
          const name = filter.value.name || ''
          params[paramKey] = name ? `${val}|${name}` : String(val)
        }
        break
      }

      default:
        break
    }
  }

  return params
}

/**
 * Deserialize URLSearchParams → appliedFilters array
 * Input: URLSearchParams object
 * Returns: [{ key, name, value }, ...]
 */
export const deserializeFiltersFromParams = (searchParams) => {
  const filters = []

  for (const [paramKey, paramValue] of searchParams.entries()) {
    if (!paramKey.startsWith(FILTER_PREFIX)) continue

    const filterKey = PARAM_TO_FILTER_KEY[paramKey]
    if (!filterKey) continue

    const filterDef = getFilterByKey(filterKey)
    if (!filterDef) continue

    let value

    switch (filterKey) {
      case FILTER_TYPES.DATE:
        value = paramValue
        break

      case FILTER_TYPES.IMC:
      case FILTER_TYPES.CREATED_BY:
        // Multi-select: semicolon-separated uid|name
        value = paramValue.split(';').map(deserializeEntity).filter(Boolean)
        break

      case FILTER_TYPES.CARRIER:
      case FILTER_TYPES.DRIVER:
        // Single entity: uid|name
        value = deserializeEntity(paramValue)
        break

      case FILTER_TYPES.EDI:
      case FILTER_TYPES.PENDING_DRIVER_ACCEPTANCE:
        value = paramValue === 'true'
        break

      case FILTER_TYPES.STREET_TURNS: {
        // Comma-separated enabled keys → object with all keys
        const enabledKeys = paramValue.split(',').filter(Boolean)
        value = {
          bundledStreetTurns: enabledKeys.includes('bundledStreetTurns'),
          interchanges: enabledKeys.includes('interchanges'),
          ordersInBundle: enabledKeys.includes('ordersInBundle'),
        }
        break
      }

      case FILTER_TYPES.LANE_LABEL: {
        // Comma-separated enabled keys → object with all keys
        const enabledKeys = paramValue.split(',').filter(Boolean)
        value = {
          laneAwarded: enabledKeys.includes('laneAwarded'),
          laneConsistent: enabledKeys.includes('laneConsistent'),
          laneNoLabel: enabledKeys.includes('laneNoLabel'),
        }
        break
      }

      case FILTER_TYPES.FREIGHT_TYPE: {
        // value|name
        const entity = deserializeEntity(paramValue)
        value = entity ? { value: entity.uid, name: entity.name } : null
        break
      }

      default:
        continue
    }

    if (value === null || value === undefined) continue

    filters.push({
      key: filterKey,
      name: filterDef.name,
      value,
    })
  }

  return filters
}

/**
 * Format filter value for display
 */
export const formatFilterValueForDisplay = (filter) => {
  const { key, value } = filter

  switch (key) {
    case FILTER_TYPES.DATE:
      return value

    case FILTER_TYPES.IMC:
    case FILTER_TYPES.CARRIER:
    case FILTER_TYPES.DRIVER:
    case FILTER_TYPES.CREATED_BY:
      if (Array.isArray(value)) {
        return value.map(getEntityDisplayName).join(' + ')
      }
      return getEntityDisplayName(value)

    case FILTER_TYPES.EDI:
      return value ? 'FROM EDI' : 'NOT FROM EDI'

    case FILTER_TYPES.PENDING_DRIVER_ACCEPTANCE:
      return 'Tendered Orders'

    case FILTER_TYPES.STREET_TURNS:
      if (typeof value === 'object') {
        return (
          [
            value.bundledStreetTurns ? 'Bundled Street Turns' : '',
            value.interchanges ? 'Interchanges' : '',
            value.ordersInBundle ? 'Orders in Bundles' : '',
          ]
            .filter((v) => v)
            .join(' + ') || 'None'
        )
      }
      return '-'

    case FILTER_TYPES.LANE_LABEL:
      if (typeof value === 'object') {
        return (
          [
            value.laneAwarded ? 'Awarded Lane' : '',
            value.laneConsistent ? 'Consistent' : '',
            value.laneNoLabel ? 'Not Set' : '',
          ]
            .filter((v) => v)
            .join(' + ') || 'None'
        )
      }
      return '-'

    case FILTER_TYPES.FREIGHT_TYPE:
      return value?.display_name || value?.name || value || '-'

    default:
      return String(value)
  }
}
